import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLiveKit } from "@/contexts/LiveKitContext";
import { toast } from "sonner";
import {
  Mic, MicOff, Video, VideoOff, Monitor, MonitorOff,
  PhoneOff, Maximize2, Clock, Users, ChevronUp, ChevronDown,
  AlertTriangle,
} from "lucide-react";

export default function LiveKitFloatingBar() {
  const navigate = useNavigate();
  const {
    connectionState, livekitMode, activeRoomId,
    micOn, camOn, screenShare, elapsed,
    toggleMic, toggleCam, toggleScreenShare, leaveRoom, formatTime,
    lkParticipants,
  } = useLiveKit();

  const [showQuitConfirm, setShowQuitConfirm] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Only show when connected and NOT on the live meeting page
  if (connectionState !== "connected" || !activeRoomId) return null;

  // Check if we're currently on the live meeting page
  const isOnLivePage = window.location.pathname.includes("/live-meeting/");
  if (isOnLivePage) return null;

  const isLive = livekitMode === "live";

  if (collapsed) {
    return (
      <div
        className="fixed bottom-4 right-4 z-[9999] flex items-center gap-2 bg-[#0B0F19]/95 backdrop-blur-lg border border-[#1E293B] rounded-full px-3 py-2 shadow-2xl cursor-pointer hover:border-[#10B981]/50 transition-all"
        onClick={() => setCollapsed(false)}
        data-testid="floating-bar-collapsed"
      >
        <div className={`w-2 h-2 rounded-full animate-pulse ${isLive ? "bg-[#10B981]" : "bg-[#F59E0B]"}`} />
        <span className="text-white text-xs font-medium">Live</span>
        <span className="text-[#64748b] text-xs font-mono">{formatTime(elapsed)}</span>
        {micOn && <Mic className="h-3 w-3 text-[#10B981]" />}
        {!micOn && <MicOff className="h-3 w-3 text-red-400" />}
        <ChevronUp className="h-3 w-3 text-[#64748b]" />
      </div>
    );
  }

  return (
    <>
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] bg-[#0B0F19]/95 backdrop-blur-lg border border-[#1E293B] rounded-2xl px-4 py-2.5 shadow-2xl flex items-center gap-3"
        data-testid="floating-bar"
      >
        {/* Status indicator */}
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full animate-pulse ${isLive ? "bg-[#10B981]" : "bg-[#F59E0B]"}`} />
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${isLive ? "bg-[#10B981]/20 text-[#10B981]" : "bg-[#F59E0B]/20 text-[#F59E0B]"}`}>
            {isLive ? "EN DIRECT" : "SIMULATION"}
          </span>
        </div>

        {/* Timer */}
        <div className="flex items-center gap-1 text-[#94A3B8] text-xs">
          <Clock className="h-3 w-3" />
          <span className="font-mono">{formatTime(elapsed)}</span>
        </div>

        {/* Participant count */}
        <div className="flex items-center gap-1 text-[#94A3B8] text-xs">
          <Users className="h-3 w-3" />
          <span>{lkParticipants.length || "—"}</span>
        </div>

        {/* Separator */}
        <div className="w-px h-5 bg-[#2A3441]" />

        {/* Media controls */}
        <button
          onClick={toggleMic}
          className={`p-1.5 rounded-lg transition-all ${micOn ? "bg-[#1E293B] text-[#10B981] hover:bg-[#2A3441]" : "bg-red-500/20 text-red-400 hover:bg-red-500/30"}`}
          title={micOn ? "Couper le micro" : "Activer le micro"}
          data-testid="float-toggle-mic"
        >
          {micOn ? <Mic className="h-3.5 w-3.5" /> : <MicOff className="h-3.5 w-3.5" />}
        </button>

        <button
          onClick={toggleCam}
          className={`p-1.5 rounded-lg transition-all ${camOn ? "bg-[#1E293B] text-[#10B981] hover:bg-[#2A3441]" : "bg-red-500/20 text-red-400 hover:bg-red-500/30"}`}
          title={camOn ? "Couper la camera" : "Activer la camera"}
          data-testid="float-toggle-cam"
        >
          {camOn ? <Video className="h-3.5 w-3.5" /> : <VideoOff className="h-3.5 w-3.5" />}
        </button>

        <button
          onClick={toggleScreenShare}
          className={`p-1.5 rounded-lg transition-all ${screenShare ? "bg-[#3B82F6]/20 text-[#3B82F6]" : "bg-[#1E293B] text-[#64748b] hover:bg-[#2A3441]"}`}
          title={screenShare ? "Arreter le partage" : "Partager l'ecran"}
          data-testid="float-toggle-screen"
        >
          {screenShare ? <Monitor className="h-3.5 w-3.5" /> : <MonitorOff className="h-3.5 w-3.5" />}
        </button>

        {/* Separator */}
        <div className="w-px h-5 bg-[#2A3441]" />

        {/* Return to live page */}
        <button
          onClick={() => navigate(`/app/live-meeting/${activeRoomId}`)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#10B981]/15 text-[#10B981] text-xs font-semibold hover:bg-[#10B981]/25 transition-all"
          title="Revenir a la conference"
          data-testid="float-return-live"
        >
          <Maximize2 className="h-3.5 w-3.5" />
          Revenir au Live
        </button>

        {/* Quit */}
        <button
          onClick={() => setShowQuitConfirm(true)}
          className="p-1.5 rounded-lg bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-all"
          title="Quitter la conference"
          data-testid="float-quit"
        >
          <PhoneOff className="h-3.5 w-3.5" />
        </button>

        {/* Collapse */}
        <button
          onClick={() => setCollapsed(true)}
          className="p-1 text-[#64748b] hover:text-white transition-colors"
          title="Reduire"
          data-testid="float-collapse"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Quit confirmation modal */}
      {showQuitConfirm && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="quit-confirm-modal">
          <div className="bg-[#151B2B] border border-[#2A3441] rounded-2xl p-6 max-w-sm mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Quitter la conference ?</h3>
                <p className="text-[#64748b] text-xs mt-0.5">Votre camera, micro et partage d'ecran seront coupes.</p>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setShowQuitConfirm(false)}
                className="flex-1 px-4 py-2 rounded-xl bg-[#1E293B] text-[#94A3B8] text-sm font-medium hover:bg-[#2A3441] transition-colors"
                data-testid="quit-cancel"
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  leaveRoom();
                  setShowQuitConfirm(false);
                  toast.success("Vous avez quitte la conference");
                }}
                className="flex-1 px-4 py-2 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors"
                data-testid="quit-confirm"
              >
                Quitter
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
